import { ref } from 'vue'

/**
 * 确认对话框 —— 替代 `ElMessageBox.confirm`。
 *
 * **签名与语义刻意保持一致**：`confirm(message, title, options)`，
 * 确认时 resolve、取消时 **reject**。全站 21 处调用都写成
 * `try { await confirm(...) } catch { return }`，改语义就要改 21 个地方，
 * 每处都可能把「取消」误判成「确认」—— 那是删除类操作，代价太大。
 *
 * ## resolve 的值是那个勾选框的状态（2026-08-01 加）
 *
 * 从 `void` 变成 `boolean` 是**加信息**，不是改语义：resolve 仍然只表示
 * 「用户确认了」。既有 21 处 `await confirm(...)` 不看返回值，一个字都不用改。
 */

/**
 * 对话框里的一个附加勾选项 —— 用于「同一个动作，但后果可以更重」的场合。
 *
 * **默认永远不勾**（这里没有 `defaultChecked`，是故意的）。
 * 一个预先勾好的破坏性选项，会让「删登记」在用户眼里还是那个熟悉的操作，
 * 而实际执行的是「删数据」—— 那正是这个对话框想拦住的事。
 *
 * 为什么不做成两个菜单项：勾选框把「要不要连数据一起删」放在用户**已经在
 * 决定删不删**的那一刻，而两个并列的菜单项要用户先读懂两者的差别才敢点。
 */
export interface ConfirmCheckbox {
  label: string
  /** 勾上之后**多失去什么**。不可逆的话这里必须说出来。 */
  hint?: string
}

export interface ConfirmOptions {
  confirmButtonText?: string
  cancelButtonText?: string
  /** 'warning' | 'error' 会把确认按钮变成危险色。与 EP 的 type 同名，便于直接沿用。 */
  type?: 'info' | 'success' | 'warning' | 'error'
  /** 见 `ConfirmCheckbox`。不给就是普通确认框。 */
  checkbox?: ConfirmCheckbox
}

interface PendingConfirm extends ConfirmOptions {
  message: string
  title: string
  resolve: (checked: boolean) => void
  reject: (reason?: unknown) => void
}

const pending = ref<PendingConfirm | null>(null)

export function useConfirm() {
  return function confirm(
    message: string,
    title = '确认',
    options: ConfirmOptions = {},
  ): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      pending.value = { message, title, resolve, reject, ...options }
    })
  }
}

/** 仅供 ConfirmHost 使用。 */
export function useConfirmState() {
  function accept(checked = false): void {
    pending.value?.resolve(checked)
    pending.value = null
  }
  function dismiss(): void {
    // reject 而不是 resolve(false)：见上面的语义说明。
    pending.value?.reject(new Error('cancel'))
    pending.value = null
  }
  return { pending, accept, dismiss }
}
